import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import { waLink } from "@/lib/api";

const TIERS = [
  {
    key: "plot",
    name: "Vacant Plot Shield",
    monthly: 625, annual: 7500,
    desc: "For vacant plots, farm lands and investment land parcels.",
    features: [
      "Scheduled site visits",
      "Geo-tagged photos",
      "Boundary & fencing status check",
      "Encroachment risk observation",
      "Debris / dumping alerts",
      "WhatsApp / email report",
      "Annual property condition summary",
    ],
    best: "Plots in OMR, ECR, Kelambakkam, Tambaram, Guduvanchery, Chengalpattu, Oragadam, Avadi.",
    cta: "Protect My Plot",
    testid: "pricing-tier-plot",
  },
  {
    key: "vacant",
    name: "Vacant Home Watch",
    monthly: 1250, annual: 15000,
    desc: "For locked apartments, villas and independent houses.",
    features: [
      "Periodic property inspection",
      "Door, window and visible damage check",
      "Seepage and leakage observation",
      "EB meter / common area observation",
      "Society / security interaction",
      "Photo and video updates",
      "Maintenance recommendation report",
    ],
    best: "Owners who keep their flat or house vacant for future use, resale or occasional visits.",
    cta: "Monitor My Vacant Home",
    testid: "pricing-tier-vacant",
  },
  {
    key: "rental",
    name: "Comprehensive Rental Management",
    monthly: 1250, annual: 13500,
    desc: "For rented apartments, houses, villas and commercial spaces.",
    featured: true,
    features: [
      "Tenant coordination",
      "Rent follow-up",
      "Rental agreement support",
      "Move-in inventory report",
      "Periodic inspection",
      "Maintenance ticket coordination",
      "Vendor estimate support",
      "Move-out inspection support",
      "Owner reporting",
    ],
    best: "NRIs, landlords, investors and busy owners who want rental income without daily follow-up stress.",
    cta: "Manage My Rental Property",
    testid: "pricing-tier-rental",
    badge: "Most Preferred by NRIs",
  },
  {
    key: "concierge",
    name: "Premium Concierge Care",
    custom: true,
    desc: "For NRIs, senior citizens and families needing complete local representation.",
    features: [
      "Dedicated relationship manager",
      "Property tax / EB / society dues reminders",
      "Vendor coordination",
      "Legal / documentation coordination",
      "Emergency visit support",
      "Family update calls",
      "Repair and renovation supervision",
      "Quarterly property health report",
    ],
    best: "Families abroad, senior citizens and owners who need trusted local support beyond standard management.",
    cta: "Speak to a Chennai Director",
    testid: "pricing-tier-concierge",
  },
];

export default function Pricing() {
  const [billing, setBilling] = useState("annual"); // annual | monthly
  const fmt = (n) => `₹${n.toLocaleString("en-IN")}`;
  return (
    <div data-testid="page-pricing">
      <section className="bg-brand-navy text-white">
        <div className="container-x py-20 md:py-28">
          <div className="eyebrow !text-brand-cyan">Transparent Pricing</div>
          <h1 className="mt-3 font-display text-4xl md:text-6xl font-bold leading-tight max-w-4xl">
            Property care plans. No confusion. No hidden surprises.
          </h1>
          <p className="mt-5 max-w-2xl text-white/80">
            Choose the level of support your property needs — from basic plot monitoring to complete rental management and premium concierge care.
          </p>

          <div className="mt-9 inline-flex bg-white p-1 text-brand-navy text-xs uppercase tracking-[0.2em] font-semibold">
            {["monthly", "annual"].map((b) => (
              <button
                key={b}
                data-testid={`pricing-toggle-${b}`}
                onClick={() => setBilling(b)}
                className={`px-5 py-2 transition ${billing === b ? "bg-brand-cyan text-white" : "text-brand-navy"}`}
              >
                {b === "annual" ? "Annual · Save more" : "Monthly"}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="section-pad">
        <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-6">
          {TIERS.map((t) => (
            <article key={t.key} data-testid={t.testid}
              className={`relative card-flat p-7 flex flex-col ${t.featured ? "border-2 border-brand-cyan xl:-translate-y-3 shadow-xl" : ""}`}>
              {t.badge && (
                <div className="absolute -top-3 left-7 bg-brand-cyan text-white text-[10px] uppercase tracking-[0.22em] font-bold px-3 py-1">
                  {t.badge}
                </div>
              )}
              <div className="eyebrow">{t.name}</div>
              <p className="mt-2 text-sm text-brand-slate/80">{t.desc}</p>
              <div className="mt-5">
                {t.custom ? (
                  <div className="font-display text-4xl font-bold text-brand-navy">Custom</div>
                ) : (
                  <div className="flex items-baseline gap-1">
                    <span className="font-display text-4xl font-bold text-brand-navy">
                      {fmt(billing === "annual" ? t.annual : t.monthly)}
                    </span>
                    <span className="text-sm text-brand-slate/70">{billing === "annual" ? "/ year" : "/ month"}</span>
                  </div>
                )}
                <div className="text-[10px] text-brand-slate/60 mt-1">+ GST applicable</div>
              </div>
              <ul className="mt-5 space-y-2 text-sm text-brand-slate/85">
                {t.features.map((f) => (
                  <li key={f} className="flex items-start gap-2"><Check size={16} className="text-brand-cyan mt-0.5" /> {f}</li>
                ))}
              </ul>
              <div className="mt-5 text-xs text-brand-slate/60"><b className="text-brand-navy">Best for:</b> {t.best}</div>
              <Link to="/contact" className={`${t.featured ? "btn-cyan" : "btn-ghost-navy"} mt-6 w-full`} data-testid={`tier-cta-${t.key}`}>{t.cta}</Link>
            </article>
          ))}
        </div>

        <div className="mt-16 bg-brand-navy text-white p-10 md:p-14 grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <div className="eyebrow !text-brand-cyan">Looking for something custom?</div>
            <h2 className="mt-3 font-display text-3xl md:text-4xl font-bold">Every property is different. So is its care plan.</h2>
            <p className="mt-3 text-white/80">A vacant plot in Chengalpattu, a rented apartment in Velachery and an independent house in Anna Nagar may need different levels of care. Chat with our Chennai directors instantly.</p>
          </div>
          <div className="md:col-span-4 flex flex-col gap-3">
            <a
              href={waLink("Hi Hi-Tech Realty, I want a custom property care plan.")}
              target="_blank" rel="noreferrer"
              className="btn-whatsapp w-full" data-testid="pricing-whatsapp">
              WhatsApp for Custom Plan
            </a>
            <Link to="/contact" className="btn-cyan w-full">Book a Property Review <ArrowRight size={14} /></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
